import { C } from '../../lib/tokens.js';
import { useUserCache } from '../../lib/UserCacheContext.jsx';
import { useSelfUser, useAvatarUrl } from '../../lib/selfUsers.js';

// One palette, keyed by name (not role) — so the same person gets the same color
// everywhere, regardless of which code path built the user object they're rendered from.
const PALETTE = ['#223469', '#1F94C8', '#3F7A00', '#A86600', '#9E0617', '#5A3E85'];

function colorFor(key) {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
}

export function Avatar({ user, size = 28, ring = false }) {
  const { cache } = useUserCache();
  const id = typeof user === 'string' ? user : null;
  const ctxUser = id ? (cache[id] ?? null) : null;
  // Falls back to a self-fetch (see selfUsers.js) when the Context never resolves
  // this id - a cross-bundle Context mismatch.
  const { user: selfUser } = useSelfUser(id, !ctxUser);
  const u = id ? (ctxUser ?? selfUser) : user;
  // Loaded for every user with an avatar_url - whether this got an id or a user object.
  const blobUrl = useAvatarUrl(u?.avatar_url);

  if (!u) return null;

  const shape = {
    width: size, height: size, borderRadius: '50%', flex: 'none', overflow: 'hidden',
    boxShadow: ring ? `0 0 0 2px #fff, 0 0 0 3px ${C.mercury}` : 'none',
  };

  if (blobUrl) {
    return <img src={blobUrl} alt={u.name} title={u.name} style={{ ...shape, objectFit: 'cover', display: 'block' }} />;
  }

  const key = u.name || u.initials || String(u.id ?? '');
  return (
    <div title={u.name} style={{
      ...shape, background: colorFor(key), color: '#fff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.4), fontWeight: 500,
    }}>{u.initials}</div>
  );
}
